import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Dialog as RadixDialog, DialogContent as RadixDialogContent, DialogHeader as RadixDialogHeader, DialogTitle as RadixDialogTitle } from "../shadcn/ui/dialog";
import { spaceManagementService } from "../services/host-apiServices/spaceManagementService";
import logger from "../utils/logger";

const Dialog = RadixDialog as any;
const DialogContent = RadixDialogContent as any;
const DialogHeader = RadixDialogHeader as any;
const DialogTitle = RadixDialogTitle as any;

// ---------------------------------------------------------------------------
// Constants & Types
// ---------------------------------------------------------------------------

export interface CountryItem {
  code: string;
  name: string;
  flag: string;
  currency?: string;
  defaultCity?: string;
  callingCode?: string;
}

export const DEFAULT_SUPPORTED_COUNTRIES: CountryItem[] = [
  { code: "GH", name: "Ghana", flag: "🇬🇭", defaultCity: "Accra", currency: "GHS" },
  { code: "KE", name: "Kenya", flag: "🇰🇪", defaultCity: "Nairobi", currency: "KES" },
];

export const SUPPORTED_COUNTRIES = DEFAULT_SUPPORTED_COUNTRIES;

export type SupportedCountryCode = string;

export const BROWSE_COUNTRY_KEY = "userBrowseCountry";

// Dynamic cache for fetched countries so helper functions can access them
let dynamicSupportedCountries: CountryItem[] = [...DEFAULT_SUPPORTED_COUNTRIES];

export function setDynamicSupportedCountries(countries: CountryItem[]): void {
  if (countries && countries.length > 0) {
    dynamicSupportedCountries = countries;
  }
}

export function getDynamicSupportedCountries(): CountryItem[] {
  return dynamicSupportedCountries;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function saveBrowseCountry(code: SupportedCountryCode): void {
  try {
    localStorage.setItem(BROWSE_COUNTRY_KEY, code);
    window.dispatchEvent(new CustomEvent("browseCountryChanged", { detail: code }));
  } catch { /* ignore */ }
}

export function clearBrowseCountry(): void {
  try {
    localStorage.removeItem(BROWSE_COUNTRY_KEY);
    window.dispatchEvent(new CustomEvent("browseCountryChanged", { detail: null }));
  } catch { /* ignore */ }
}

export function getSavedBrowseCountry(detectedCountry?: string): SupportedCountryCode {
  try {
    const val = localStorage.getItem(BROWSE_COUNTRY_KEY);
    const list = getDynamicSupportedCountries();
    if (val && list.some(c => c.code?.toUpperCase() === val.toUpperCase())) return val;
    if (detectedCountry && list.some(c => c.code?.toUpperCase() === detectedCountry.toUpperCase())) {
      saveBrowseCountry(detectedCountry);
      return detectedCountry;
    }
  } catch { /* ignore */ }
  const list = getDynamicSupportedCountries();
  return list[0]?.code || "GH";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CountryPickerBannerProps {
  detectedCountry?: string;
  onCountrySelect: (code: SupportedCountryCode) => void;
  onDismiss?: () => void;
  forceOpen?: boolean;
}

const CountryPickerBanner: React.FC<CountryPickerBannerProps> = ({
  detectedCountry,
  onCountrySelect,
  onDismiss,
  forceOpen = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [selectedCode, setSelectedCode] = useState<SupportedCountryCode>(() => {
    return getSavedBrowseCountry(detectedCountry);
  });
  const [countryList, setCountryList] = useState<CountryItem[]>(getDynamicSupportedCountries());

  useEffect(() => {
    let isMounted = true;
    spaceManagementService
      .getSupportedCountries()
      .then(res => {
        if (!isMounted || !res?.data) return;
        const apiData = Array.isArray(res.data) ? res.data : (res.data as any).data || [];
        if (apiData.length > 0) {
          const mapped: CountryItem[] = apiData
            .map((item: any) => {
              const code = item.code || item.filterValue || item.name;
              if (!code) return null;
              return {
                code: String(code),
                name: item.name || item.filterValue || String(code),
                flag: item.flag || "🌍",
                currency: item.currency,
                defaultCity: item.defaultCity,
                callingCode: item.callingCode,
              };
            })
            .filter(Boolean) as CountryItem[];

          if (mapped.length > 0) {
            setCountryList(mapped);
            setDynamicSupportedCountries(mapped);
          }
        }
      })
      .catch(err => {
        logger.warn("Failed to fetch supported countries from API, using fallback", err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setVisible(forceOpen);
  }, [forceOpen]);

  const handleSelect = (code: SupportedCountryCode) => {
    setSelectedCode(code);
    saveBrowseCountry(code);
    setVisible(false);
    onCountrySelect(code);
  };

  const handleClose = (open: boolean) => {
    setVisible(open);
    if (!open) {
      onDismiss?.();
    }
  };

  return (
    <Dialog open={visible} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-2xl p-6 bg-white border border-gray-100 shadow-2xl">
        <DialogHeader className="pb-3 border-b border-gray-100">
          <DialogTitle className="text-lg font-bold text-gray-900 text-left">
            Choose Your Market
          </DialogTitle>
          <p className="text-xs text-gray-500 text-left mt-0.5">
            Select a region to explore available spaces and services.
          </p>
        </DialogHeader>

        <div className="space-y-2 pt-3">
          {countryList.map((c) => {
            const isSelected = selectedCode?.toUpperCase() === c.code?.toUpperCase();
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => handleSelect(c.code)}
                className={`w-full flex items-center justify-between p-3.5 rounded-xl border text-left transition-all ${
                  isSelected
                    ? "bg-blue-50/70 border-blue-600/30 text-blue-950 shadow-sm"
                    : "bg-white border-gray-100 text-gray-700 hover:bg-gray-50/80 hover:border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3.5">
                  <span className="text-2xl p-1.5 bg-gray-50 rounded-lg border border-gray-100/80">{c.flag}</span>
                  <div className="font-semibold text-sm text-gray-900">{c.name}</div>
                </div>
                {isSelected && (
                  <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-sm">
                    <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CountryPickerBanner;
